import React from 'react';
import Image from 'next/image';
import Dries from '../../../public/assets/dries.png';
import Carlo from '../../../public/assets/carlo.png';
import Script from 'next/script';
import {NextSeo} from 'next-seo';
import {BlogFooter, Footer, Navbar} from '../../components';
import {GetStaticProps, InferGetStaticPropsType} from 'next';
import Head from 'next/head';
import {getPost, getPosts} from '../../lib/ghost';

/**
 *
 * @param root0
 */
export default function Home({post}: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <div>
      <Script
        id={`blog-breadcrumb-schema-${post.slug}`}
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'BreadcrumbList',
            'itemListElement': [
              {
                '@type': 'ListItem',
                'position': 1,
                'name': 'Blog',
                'item': 'https://www.useplunk.com/blog',
              },
              {
                '@type': 'ListItem',
                'position': 2,
                'name': post.title,
                'item': `https://www.useplunk.com/blog/${post.slug}`,
              },
            ],
          }),
        }}
      />

      <Script
        id={`blog-schema-${post.slug}`}
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            'headline': post.title,
            'image': post.feature_image,
            'author': {
              '@type': 'Person',
              'name': post.primary_author.name,
            },
            'publisher': {
              '@type': 'Organization',
              'name': 'Plunk',
              'logo': {
                '@type': 'ImageObject',
                'url': 'https://www.useplunk.com/assets/logo.png',
              },
            },
            'datePublished': post.created_at,
          }),
        }}
      />

      <NextSeo
        title={`${post.title} - Plunk Blog`}
        description={post.excerpt}
        twitter={{cardType: 'summary_large_image', handle: '@useplunk', site: '@useplunk'}}
        openGraph={{
          title: `${post.title} - Plunk Blog`,
          description: post.excerpt,
          images: [
            {
              url: post.feature_image,
              alt: 'Plunk',
            },
          ],
          url: `https://www.useplunk.com/blog/${post.slug}`,
          article: {
            publishedTime: post.created_at,
            section: 'Blog',
          },
        }}
        additionalMetaTags={[
          {property: 'title', content: `${post.title} - Plunk Blog`},
          {
            property: 'keywords',
            content: `plunk,blog`,
          },
        ]}
      />

      <Head>
        <link rel="alternate" type="application/rss+xml" href="https://www.useplunk.com/rss/blog/feed.xml" />
        <link rel="alternate" type="application/rss+json" href="https://www.useplunk.com/rss/blog/feed.json" />
      </Head>

      <Navbar />

      <article className={'prose prose-neutral mx-auto mt-9 max-w-2xl px-8 xl:px-0'}>
        <h1>{post.title}</h1>
        <section dangerouslySetInnerHTML={{__html: post.html}}></section>

        <div className={'flex items-center gap-3'}>
          <Image
            className="h-14 w-14 rounded-full"
            src={post.primary_author.name === 'Dries Augustyns' ? Dries : Carlo}
            alt={post.primary_author.name === 'Dries Augustyns' ? 'Dries Augustyns' : "Carlo D'Agnolo"}
          />
          <div className={''}>
            <span className={'block font-semibold text-neutral-800'}>{post.primary_author.name}</span>
            <span className={'block text-sm text-neutral-600'}>
              {post.primary_author.name === 'Dries Augustyns' ? 'Founder & Technical Lead' : 'Founder & Head of Growth'}{' '}
              at Plunk
            </span>
          </div>
        </div>

        <BlogFooter slug={post.slug} name={post.title} />
      </article>
      <Footer />
    </div>
  );
}

export const getStaticProps: GetStaticProps = async ({params}) => {
  const post = await getPost(params?.slug as string);

  return {
    props: {
      post,
    },
    revalidate: 3600,
  };
};

export const getStaticPaths = async () => {
  const posts = await getPosts();

  return {
    paths: posts.map(post => ({
      params: {
        slug: post.slug,
      },
    })),
    fallback: 'blocking',
  };
};
